import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useGetQuery } from '../../api/aws/useGetQuery'
import { useActiveUser } from '../../api/slack/useActiveUser'
import GroboticLight from '../../images/Grobotic png-lightmode.png'
import GroboticPng from '../../images/Grobotic png.png'
import { BoxScroller, CreateProjectTextField } from '../../styles/elements/styleElements'
import AdditionalContactsAutocomplete from '../Contacts/AdditionalContactsAutocomplete'
import PrimaryContactAutocomplete from '../Contacts/PrimaryContactAutocomplete'
import { housingTypeOptions, typeOptions } from '../CreateOrganization/CreateOrganization'
import CustomParentCompanyAutocomplete from '../CustomCompanyComponents/CustomParentCompanyAutocomplete'
import CustomVerticalAutocomplete from '../CustomCompanyComponents/CustomVerticalAutocomplete'
import DialogHeaderComponent from '../DialogHeader/DialogHeader'
import CustomTypeSelect from '../shared/Select/CustomTypeSelect'
import { dialogContentStyles, dialogStyles } from './styles'

const DialogIcon = styled('img')({
  width: 50,
  height: 50,
  borderRadius: '50%',
})

const CreateDealOrgDialog = ({ open, onClose, tempInput, onSave }) => {
  const { activeUser, isActiveUserLoading, isActiveUserError } = useActiveUser()
  const [selectedParentClient, setSelectedParentClient] = useState([])
  const { data: contacts, isLoading: contactsLoading, isError: contactsError } = useGetQuery(
    '/aws/contacts',
    'contacts'
  )
  const theme = useTheme()
  const dialogIcon = theme.palette.mode === 'dark' ? GroboticPng : GroboticLight
  const initialState = {
    name: '',
    dba: '',
    type: '',
    billingTerms: '',
    status: 'Prospect',
    primaryContact: [],
    contacts: [],
    teamMemberIds: [],
    parentClient: [],
    contractedBy: [],
    affiliated: [],
    dealIds: [],
    website: '',
    vertical: '',
    billingPhone: '',
    businessPhone: '',
    email: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    closeDate: '',
    cmsUsed: '',
    socialProfiles: '',
    marketingObjective: '',
    geoTargeting: '',
    summary: '',
    owner: [],
  }
  const [newOrganization, setNewOrganization] = useState({
    ...initialState,
    owner: [activeUser?.user?.id],
  })

  useEffect(() => {
    if (tempInput) {
      setNewOrganization(prev => ({ ...prev, name: tempInput }))
    }
  }, [tempInput])

  useEffect(() => {
    if (!open) {
      setNewOrganization(initialState)
    }
  }, [open])

  const handleChange = (key, eventOrValue) => {
    const value = eventOrValue.target ? eventOrValue.target.value : eventOrValue.value
    const name = eventOrValue.target ? eventOrValue.target.name : key
    setNewOrganization(prev => ({ ...prev, [name]: value }))
  }

  const handleCompanyChange = (context, value) => {
    setNewOrganization(prev => ({
      ...prev,
      [context]: value,
    }))
  }

  const handleSave = () => {
    onSave(newOrganization)
    setNewOrganization({ ...initialState, owner: [activeUser?.user?.id] })
  }

  const handleSelectingContacts = newValue => {
    setNewOrganization(prev => ({
      ...prev,
      contacts: Array.isArray(newValue)
        ? newValue.map(item => (item ? item.contacts_uuid : item.id))
        : newValue
        ? [newValue ? newValue.contacts_uuid : newValue.id]
        : [],
    }))
  }

  const handleSelectPrimaryContact = newValue => {
    setNewOrganization(prev => ({
      ...prev,
      primaryContact: Array.isArray(newValue)
        ? newValue.map(item => (item ? item.contacts_uuid : item.id))
        : newValue
        ? [newValue ? newValue.contacts_uuid : newValue.id]
        : [],
    }))
  }

  const handleVerticalChange = event => {
    const { value } = event.target
    setNewOrganization(prev => ({
      ...prev,
      vertical: value,
      contractedBy: [],
      affiliated: [],
    }))
  }

  const addParentClient = newValue => {
    setNewOrganization(prev => ({
      ...prev,
      parentClient: Array.isArray(newValue) ? newValue.map(item => item.pc_uuid) : newValue ? [newValue.pc_uuid] : [],
    }))
    setSelectedParentClient(prev => [...prev, newValue])
  }

  const handleCompanyBlur = (context, value) => {
    console.log('[CreateOrganization] handleCompanyBlur: value', value)
  }

  const verticalArray = Array.isArray(newOrganization.vertical)
    ? newOrganization.vertical
    : newOrganization.vertical
    ? [newOrganization.vertical]
    : []
  const isVerticalSelected = verticalArray.length > 0
  const isHousingSelected =
    newOrganization && verticalArray.length > 0
      ? verticalArray.some(option =>
          [
            'Real Estate - Student',
            'Real Estate - MF',
            'Real Estate - Senior',
            'Real Estate - Homebuilding',
            'Student',
            'Senior',
            'Multifamily',
            'Homebuilding',
            'Build to rent',
          ].includes(option)
        )
      : false

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: dialogStyles(theme),
      }}
    >
      <DialogHeaderComponent name="Organizations" />
      <BoxScroller>
        <DialogContent sx={dialogContentStyles}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CreateProjectTextField
                label="Name"
                name="name"
                value={newOrganization.name}
                onChange={e => handleChange('name', e)}
                fullWidth
              />
              <CreateProjectTextField
                label="DBA"
                name="dba"
                value={newOrganization.dba}
                onChange={e => handleChange('dba', e)}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <PrimaryContactAutocomplete
                contacts={contacts}
                label="Primary Contact"
                onChange={handleSelectPrimaryContact}
              />
              <AdditionalContactsAutocomplete
                contacts={contacts}
                label="Additional Contacts"
                onChange={handleSelectingContacts}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
              <CustomVerticalAutocomplete label={'Vertical'} onChange={handleVerticalChange} />
            </Box>
            {isVerticalSelected && (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                  {isHousingSelected ? (
                    <Box sx={{ flex: 1, height: '100%', minWidth: 0 }}>
                      <CustomTypeSelect
                        stateKey="type"
                        value={newOrganization.type}
                        options={housingTypeOptions}
                        displayLabel="Organization Type"
                        setterFn={setNewOrganization}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, height: '100%', minWidth: 0 }}>
                      <CustomTypeSelect
                        stateKey="type"
                        value={newOrganization.type}
                        options={typeOptions}
                        displayLabel="Organization Type"
                        setterFn={setNewOrganization}
                      />
                    </Box>
                  )}
                </Box>
                {newOrganization.type && newOrganization.type === 'Housing Property' ? (
                  <>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <CustomParentCompanyAutocomplete
                        label="Contracted By"
                        value={newOrganization.contractedBy}
                        onChange={handleCompanyChange}
                        context="contractedBy"
                        type={newOrganization.type}
                        onBlur={handleCompanyBlur}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <CustomParentCompanyAutocomplete
                        label="Affiliated"
                        value={newOrganization.affiliated}
                        onChange={handleCompanyChange}
                        context="affiliated"
                        type={newOrganization.type}
                        onBlur={handleCompanyBlur}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: 1, height: '100%', minWidth: 0 }}>
                    <CustomParentCompanyAutocomplete
                      label="Parent Client(s)"
                      value={newOrganization.parentClient}
                      onChange={handleCompanyChange}
                      context="parentClient"
                      onBlur={handleCompanyBlur}
                    />
                  </Box>
                )}
              </>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <CreateProjectTextField
                label="Website"
                name="website"
                value={newOrganization.website}
                onChange={e => handleChange('website', e)}
                fullWidth
              />
              <CreateProjectTextField
                label="Street"
                name="street"
                value={newOrganization.street}
                onChange={e => handleChange('street', e)}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <CreateProjectTextField
                  label="City"
                  name="city"
                  value={newOrganization.city}
                  onChange={e => handleChange('city', e)}
                  fullWidth
                />
                <CreateProjectTextField
                  label="State"
                  name="state"
                  value={newOrganization.state}
                  onChange={e => handleChange('state', e)}
                  fullWidth
                />
                <CreateProjectTextField
                  label="Zip"
                  name="zip"
                  value={newOrganization.zip}
                  onChange={e => handleChange('zip', e)}
                  fullWidth
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="outlined">
            Cancel
          </Button>
          <Tooltip
            title={
              !newOrganization?.vertical?.length ? (
                <Typography sx={{ color: 'text.error' }}>Vertical is required</Typography>
              ) : (
                <Typography sx={{ color: 'text.primary' }}>Add organization</Typography>
              )
            }
          >
            <div>
              <Button
                onClick={handleSave}
                sx={{
                  '&.Mui-disabled': {
                    color: !newOrganization?.vertical?.length ? 'text.errordim' : 'primary',
                  },
                }}
                variant="contained"
                disabled={!newOrganization?.vertical?.length}
              >
                Add
              </Button>
            </div>
          </Tooltip>
        </DialogActions>
      </BoxScroller>
    </Dialog>
  )
}

export default CreateDealOrgDialog
